import { Building, CalendarBlank, WarningIcon } from "@phosphor-icons/react";
import { BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AppButton } from "@libs/zapi-ui/custom";
import { TProject } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { SHADOW_200 } from "./overviewStyles";
import { formatUpdated } from "../../lib/formatUpdated";

function Tag({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 opacity-70 text-innoveraZinc-700">
      {icon}
      <span className="text-[12px] leading-[18px] font-normal">{text}</span>
    </div>
  );
}

type OverviewHeaderProps = {
  project: TProject;
  // Omitted on a runner project: its Home page has no Refresh button, since Leo's home.json is
  // the only thing that changes it.
  onRefresh?: () => void;
  isRefreshing: boolean;
};

export function OverviewHeader({ project, onRefresh, isRefreshing }: OverviewHeaderProps) {
  const { isLabs } = useMeStore();
  const { t, i18n } = useTranslation("projectHome");
  const dateLocale = i18n.language === "ja" ? "ja-JP" : "en-GB";
  const updatedLabel = formatUpdated(project.lastModified, dateLocale, (date) => t("overview.updated", { date }));

  return (
    <div className={`bg-white p-5 flex flex-col gap-2 ${SHADOW_200}`}>
      <div className="flex flex-col">
        <div className="flex items-center justify-between gap-2.5">
          <span className="text-[18px] leading-[28px] font-medium tracking-[-0.18px] text-innoveraZinc-900">
            {project.name}
          </span>
          {onRefresh && (
            <div className="flex items-center gap-2">
              {!isRefreshing && project.isOverviewStale && (
                <span
                  title={t("overview.outOfDateTooltip")}
                  className="flex items-center gap-1 text-[12px] leading-[18px] font-medium text-amber-600"
                >
                  <WarningIcon className="size-4" />
                  {t("overview.outOfDate")}
                </span>
              )}
              <AppButton
                data-testid="overview-header-refresh-button"
                appearance="outlined"
                color="zinc150"
                className="text-innoveraZinc-900 shadow-none"
                onClick={onRefresh}
                disabled={isRefreshing}
                isLoading={isRefreshing}
                loadingText={t("overview.refreshing")}
              >
                {t("overview.refresh")}
              </AppButton>
            </div>
          )}
        </div>
        {project.shortDescription && (
          <div className="py-[3px]">
            <span className="text-[12px] leading-[18px] font-normal text-innoveraZinc-700 opacity-70">
              {project.shortDescription}
            </span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-4">
        {project.organizationName && (
          <Tag icon={<Building className="size-4" weight="regular" />} text={project.organizationName} />
        )}
        {updatedLabel && <Tag icon={<CalendarBlank className="size-4" weight="regular" />} text={updatedLabel} />}
        {isLabs && <Tag icon={<BookOpen className="size-4" strokeWidth={1.5} />} text={project.projectTemplateName} />}
      </div>
    </div>
  );
}
